import EMOJI_REGEX from 'emojibase-regex/emoji';
import { jsPDF } from 'jspdf';

import { CuripodText } from '../../components/SlideComponents/slateUtil';
import { Alignment } from './renderTextElement';
import { renderLink, renderUnderLine, setPdfTextStyling } from './renderUtils';

export type MathImageToken = {
  tokenType: 'math-image';
  dataUrl: string;
  width: number;
  height: number;
  color?: string;
};

export type TextToken = CuripodText & { tokenType?: undefined };

export type ParagraphToken = TextToken | MathImageToken;

const getEmojiImageData = async (emoji: string) => {
  const codePoint = emoji.codePointAt(0);
  const hexCode = codePoint?.toString(16);
  const x = await fetch(
    `https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${hexCode}.png`,
  );
  if (!x) return null;
  return x.status === 200 ? (await x.blob()).arrayBuffer() : null;
};

function getTokenWidth(pdf: jsPDF, token: ParagraphToken): number {
  if (token.tokenType === 'math-image') return token.width;
  setPdfTextStyling({
    pdf,
    color: token.color || '#000',
    bold: !!token.bold,
    italic: !!token.italic,
  });
  return pdf.getTextDimensions(token.text).w;
}

type RenderParagraphLineProps = {
  pdf: jsPDF;
  paraLine: ParagraphToken[];
  x: number;
  y: number;
  width: number;
  alignment: Alignment;
  scale: number;
};

const renderParagraphLine = async ({
  pdf,
  x,
  y,
  width,
  paraLine,
  alignment,
}: RenderParagraphLineProps) => {
  let currentX = x;
  let currentY = y;
  let allWordsWidth = 0;

  for (const token of paraLine) {
    allWordsWidth += getTokenWidth(pdf, token);
  }

  switch (alignment.toLowerCase()) {
    case 'right':
      currentX = x + width - allWordsWidth;
      break;
    case 'center':
      currentX = x + width / 2 - allWordsWidth / 2;
      break;
    default:
      currentX = x;
      break;
  }

  for (const token of paraLine) {
    if (token.tokenType === 'math-image') {
      pdf.addImage(
        token.dataUrl,
        'PNG',
        currentX,
        currentY,
        token.width,
        token.height,
        undefined,
        'FAST',
      );
      currentX += token.width;
      continue;
    }

    pdf.saveGraphicsState();
    setPdfTextStyling({
      pdf,
      color: token.color || '#000',
      bold: !!token.bold,
      italic: !!token.italic,
    });
    const wordWidth = pdf.getTextDimensions(token.text).w;
    if (token.underline || token.link) {
      renderUnderLine({
        pdf,
        x: currentX,
        y: currentY + pdf.getTextDimensions(token.text).h,
        width: wordWidth,
      });
    }
    if (token.link) {
      renderLink({
        pdf,
        x: currentX,
        y: currentY,
        width: wordWidth,
        height: pdf.getTextDimensions(token.text).h,
        url: token.link,
      });
    }

    for (const eachChar of token.text) {
      const charWidth = pdf.getTextDimensions(eachChar).w;
      const charHeight = pdf.getTextDimensions(eachChar).h;
      const emojiExecResult = EMOJI_REGEX.exec(eachChar);
      if (emojiExecResult?.[0]) {
        const emojiImageData = await getEmojiImageData(emojiExecResult[0]);
        if (emojiImageData) {
          pdf.addImage(
            new Uint8Array(emojiImageData),
            currentX,
            currentY,
            charWidth,
            charHeight,
            undefined,
            'FAST',
          );
        } else pdf.rect(currentX, currentY, charWidth, charHeight);
      } else {
        pdf.text(eachChar, currentX, currentY + pdf.getTextDimensions(eachChar).h);
      }
      currentX = currentX + charWidth;
    }

    pdf.restoreGraphicsState();
  }

  currentY = currentY + pdf.getLineHeight();

  return { updatedX: currentX, updatedY: currentY };
};

export default renderParagraphLine;
